import { useMutation, useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, Loader2, Play, Sparkles, X } from "lucide-react";
import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { rpcClient } from "@/lib/rpc";
import { DimProposalPanel } from "./dim-proposal-panel";
import { friendlyOptimizerError } from "./optimizer-error-map";
import { DIM_LABEL, type DimSlug, type OptimizerJobState } from "./optimizer-types";

const STATUS_QK = ["/api/labs/calibration-optimizer/status"];
const HISTORY_QK = ["/api/labs/calibration-optimizer/history"];

interface ParkedCorrectionRow {
  id: string;
  evidenceId: string;
  targetKind: string;
  candidateName: string | null;
  correctedScore: string;
  originalScore: string;
  rationale: string;
  correctedAt: string;
}

interface OptimizerHistoryRow {
  jobId: string;
  actor: string;
  committedAt: string;
  dimCount: number;
  correctionCount: number;
}

function actorLabel(actor: string): string {
  if (actor === "optimizer-cron") return "automático";
  // Operator commits store the user's email; show just the local-part for brevity.
  const local = actor.split("@")[0];
  return local || actor;
}

/**
 * Top-level neurosymbolic optimizer card. Three states, one component:
 *
 *  - idle: pre-run preview. Lists parked corrections + which dims they
 *    touch + a "Propor otimização" button.
 *  - running: per-dim progress strips while the synth fits.
 *  - ready: accordion of DimProposalPanel + sticky commit bar.
 *  - committing: locked while the commit transaction lands.
 *
 * State lives on the server (file-based job state). We poll /status at
 * 2s while running, otherwise on focus. The component re-renders
 * whenever a proposal's status changes (fitting → ready → accepted).
 */
export function OptimizerCard() {
  const { toast } = useToast();
  const [confirmCommit, setConfirmCommit] = useState(false);
  const [expandedDim, setExpandedDim] = useState<DimSlug | null>(null);

  const status = useQuery<OptimizerJobState>({
    queryKey: STATUS_QK,
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/labs/calibration-optimizer/status");
      return res.json();
    },
    refetchInterval: (query) => {
      const data = query.state.data as OptimizerJobState | undefined;
      return data?.running ? 2000 : false;
    },
  });

  // Powers the "Último ciclo" footer. Refetches whenever a commit lands
  // (see commit.onSuccess) so an operator-triggered commit reflects
  // immediately, and on mount so a cron commit from overnight shows up
  // when the page is opened the next morning.
  const history = useQuery<{ entries: OptimizerHistoryRow[] }>({
    queryKey: HISTORY_QK,
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/labs/calibration-optimizer/history");
      return res.json();
    },
  });

  // Post-oRPC migration the corrections-audit endpoint lives only at
  // `/rpc/labs/correctionsAuditList` — the REST URL the previous fetch
  // targeted didn't exist, so the request fell through to the Vite SPA,
  // failed to parse, and the empty state ("Nenhuma correção pendente.")
  // rendered next to the calibration-runs-section badge that correctly
  // counts 10. Switch to the typed client, matching audit-section.tsx.
  const parked = useQuery<ParkedCorrectionRow[]>({
    queryKey: ["labs", "correctionsAuditList", { status: "parked", legacy: true }],
    queryFn: () =>
      rpcClient.labs.correctionsAuditList({
        status: "parked",
        limit: 200,
        legacy: true,
      }) as Promise<ParkedCorrectionRow[]>,
  });

  const start = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/labs/calibration-optimizer/start", {});
      const body = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
      return body;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STATUS_QK });
      toast({ title: "Otimização iniciada", description: "Acompanhe o progresso abaixo." });
    },
    onError: (err: Error) => {
      toast({
        title: "Falha ao iniciar",
        description: friendlyOptimizerError(err.message),
        variant: "destructive",
      });
    },
  });

  const cancel = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/labs/calibration-optimizer/cancel", {});
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STATUS_QK });
      toast({ title: "Ciclo cancelado" });
    },
  });

  const commit = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/labs/calibration-optimizer/commit", {});
      const body = (await res.json().catch(() => ({}))) as {
        error?: string;
        committedDims?: string[];
        appliedCorrectionIds?: string[];
      };
      if (!res.ok) throw new Error(body.error ?? `HTTP ${res.status}`);
      return body;
    },
    onSuccess: (result) => {
      setConfirmCommit(false);
      queryClient.invalidateQueries({ queryKey: STATUS_QK });
      queryClient.invalidateQueries({ queryKey: HISTORY_QK });
      queryClient.invalidateQueries({ queryKey: ["/api/labs/corrections-audit"] });
      queryClient.invalidateQueries({ queryKey: ["/api/labs/calibration-runs/summary"] });
      toast({
        title: "Otimização aplicada",
        description: `${result.committedDims?.length ?? 0} rubrica${
          (result.committedDims?.length ?? 0) === 1 ? "" : "s"
        } atualizada${(result.committedDims?.length ?? 0) === 1 ? "" : "s"}, ${
          result.appliedCorrectionIds?.length ?? 0
        } correç${
          (result.appliedCorrectionIds?.length ?? 0) === 1 ? "ão" : "ões"
        } fechada${(result.appliedCorrectionIds?.length ?? 0) === 1 ? "" : "s"}.`,
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Falha no commit",
        description: friendlyOptimizerError(err.message),
        variant: "destructive",
      });
    },
  });

  const state = status.data;
  const running = state?.running === true;
  const ready = !running && state && state.proposals.length > 0 && !state.fatalError;
  const acceptedCount = state?.proposals.filter((p) => p.status === "accepted").length ?? 0;

  // Auto-collapse a dim once it's decided (clean review surface). User can
  // re-expand if they want to revisit before commit.
  useEffect(() => {
    if (!state) return;
    if (expandedDim) {
      const cur = state.proposals.find((p) => p.dim === expandedDim);
      if (cur && (cur.status === "accepted" || cur.status === "rejected")) {
        setExpandedDim(null);
      }
    }
  }, [state, expandedDim]);

  return (
    <section
      className="calib-op-card optimizer-card risk-care"
      aria-labelledby="calib-op-optimizer"
      data-testid="calib-op-optimizer"
    >
      <header className="calib-op-head">
        <span className="calib-op-kicker">01 · otimização</span>
        <h3 id="calib-op-optimizer" className="calib-op-title">
          Otimização neurosimbólica
        </h3>
      </header>

      <p className="calib-op-body">
        Pega <strong>todas as correções pendentes</strong> de uma vez, descobre quais rubricas elas
        afetam, e propõe novas regras para cada rubrica combinando sinais estruturais do perfil com
        análise LLM. Você revê as mudanças, aceita / recusa / pede para refazer, e tudo vira um
        único commit. As notas antigas ficam preservadas; só avaliações novas usam a nova rubrica.
      </p>

      {!ready && !running && <IdlePreview parked={parked.data ?? []} start={start} />}

      {!ready && !running && <LastCycleFooter entry={history.data?.entries?.[0]} />}

      {running && state && <RunningPanel state={state} cancel={cancel} />}

      {ready && state && (
        <>
          <DimAccordion state={state} expandedDim={expandedDim} onExpand={setExpandedDim} />
          <div className="optimizer-commit-bar">
            <div className="optimizer-commit-summary">
              <strong>{acceptedCount}</strong> dim{acceptedCount === 1 ? "" : "s"} aceita
              {acceptedCount === 1 ? "" : "s"} · {(() => {
                // Pre-fix, the bar said all `correctionsSnapshot`
                // entries "fecharão como aplicadas" even though the
                // commit step now only marks the ones the accepted
                // rubric actually hits (withinTolerance === true). Use
                // the same partition logic the server does so the
                // operator sees an honest count before clicking
                // "Aplicar".
                const hit = new Set<string>();
                for (const p of state.proposals) {
                  if (p.status !== "accepted") continue;
                  for (const c of p.correctionsCovered) {
                    if (c.withinTolerance === true) hit.add(c.correctionId);
                  }
                }
                const willClose = state.correctionsSnapshot.filter((id) => hit.has(id)).length;
                const total = state.correctionsSnapshot.length;
                const remaining = total - willClose;
                return (
                  <>
                    <strong>{willClose}</strong> de {total} correç
                    {total === 1 ? "ão" : "ões"} fecharão como aplicada
                    {willClose === 1 ? "" : "s"}
                    {remaining > 0 && (
                      <>
                        {" "}
                        ·{" "}
                        <span className="optimizer-commit-remainder">
                          {remaining} permanece{remaining === 1 ? "" : "m"} pendente
                          {remaining === 1 ? "" : "s"}
                        </span>
                      </>
                    )}
                  </>
                );
              })()}
            </div>
            <div className="optimizer-commit-actions">
              <Button
                variant="outline"
                size="sm"
                onClick={() => cancel.mutate()}
                disabled={cancel.isPending}
                data-testid="optimizer-cancel"
              >
                <X className="h-3.5 w-3.5 mr-2" aria-hidden />
                Descartar ciclo
              </Button>
              <Button
                size="sm"
                onClick={() => setConfirmCommit(true)}
                disabled={commit.isPending || acceptedCount === 0}
                data-testid="optimizer-commit"
              >
                {commit.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" aria-hidden />
                ) : (
                  <Sparkles className="h-3.5 w-3.5 mr-2" aria-hidden />
                )}
                Aplicar {acceptedCount} otimizaç{acceptedCount === 1 ? "ão" : "ões"}
              </Button>
            </div>
          </div>
        </>
      )}

      {state?.fatalError && (
        <div className="optimizer-fatal-error">
          <strong>Erro:</strong> {state.fatalError}
          <Button size="sm" variant="outline" onClick={() => cancel.mutate()}>
            Descartar
          </Button>
        </div>
      )}

      <AlertDialog
        open={confirmCommit}
        onOpenChange={(v) => {
          if (!v && !commit.isPending) setConfirmCommit(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Aplicar {acceptedCount} otimizaç{acceptedCount === 1 ? "ão" : "ões"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Vai sobrescrever {acceptedCount === 1 ? "a rubrica" : `as ${acceptedCount} rubricas`}{" "}
              aceitas no banco de dados e fechar {state?.correctionsSnapshot.length ?? 0} correç
              {state?.correctionsSnapshot.length === 1 ? "ão" : "ões"} como aplicada
              {state?.correctionsSnapshot.length === 1 ? "" : "s"}. Operação atômica — ou tudo
              passa, ou nada muda.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <p className="text-xs text-muted-foreground">
            Avaliações antigas mantêm o resultado anterior. Apenas avaliações novas usarão a nova
            rubrica.
          </p>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={commit.isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => commit.mutate()} disabled={commit.isPending}>
              {commit.isPending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />
                  Aplicando…
                </>
              ) : (
                "Aplicar"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function IdlePreview({
  parked,
  start,
}: {
  parked: ParkedCorrectionRow[];
  start: { mutate: () => void; isPending: boolean };
}) {
  if (parked.length === 0) {
    return (
      <div className="optimizer-idle">
        <p className="text-sm text-muted-foreground">
          Nenhuma correção pendente. Quando a CHRO marcar ajustes nas avaliações, eles aparecem aqui
          para você rodar o ciclo.
        </p>
        <Button disabled data-testid="optimizer-start">
          <Play className="h-4 w-4 mr-2" aria-hidden />
          Propor otimização
        </Button>
      </div>
    );
  }
  const dims = previewAffectedDims(parked);
  return (
    <div className="optimizer-idle">
      <div className="optimizer-idle-stats">
        <strong className="tabular-nums">{parked.length}</strong> correç
        {parked.length === 1 ? "ão" : "ões"} pendente{parked.length === 1 ? "" : "s"} · afetam{" "}
        <strong>{dims.length}</strong> rubricas
      </div>
      <ul className="optimizer-idle-dims">
        {dims.map((d) => (
          <li key={d.dim}>
            <strong>{DIM_LABEL[d.dim]}</strong>{" "}
            <span className="text-muted-foreground">({d.count})</span>
          </li>
        ))}
      </ul>
      <details className="optimizer-idle-corrections">
        <summary>Ver as {parked.length} correções pendentes</summary>
        <table className="optimizer-idle-corrections-table">
          <thead>
            <tr>
              <th>Candidato</th>
              <th>Rubrica</th>
              <th className="text-right">Original</th>
              <th className="text-right">Corrigido</th>
              <th>Motivo</th>
              <th>Quando</th>
            </tr>
          </thead>
          <tbody>
            {parked.map((p) => (
              <tr key={p.id}>
                <td>{p.candidateName ?? <em>(perfil removido)</em>}</td>
                <td>
                  <code>{p.evidenceId}</code>
                </td>
                <td className="text-right tabular-nums">{p.originalScore}</td>
                <td className="text-right tabular-nums">
                  <strong>{p.correctedScore}</strong>
                </td>
                <td title={p.rationale}>{truncate(p.rationale, 60)}</td>
                <td className="text-muted-foreground">{formatWhen(p.correctedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
      <Button
        onClick={() => start.mutate()}
        disabled={start.isPending}
        data-testid="optimizer-start"
      >
        {start.isPending ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden />
        ) : (
          <Play className="h-4 w-4 mr-2" aria-hidden />
        )}
        Propor otimização
      </Button>
      <p className="text-xs text-muted-foreground">
        ~30s a 2min por rubrica afetada · custa LLM apenas se você refizer pedindo ajustes em
        linguagem natural
      </p>
    </div>
  );
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return `${s.slice(0, n - 1)}…`;
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  const diffMin = Math.round((Date.now() - d.getTime()) / 60_000);
  if (diffMin < 1) return "agora";
  if (diffMin < 60) return `${diffMin}min atrás`;
  const hours = Math.floor(diffMin / 60);
  if (hours < 24) return `${hours}h atrás`;
  const days = Math.floor(hours / 24);
  return `${days}d atrás`;
}

/**
 * Footer rendered when the card is idle — shows the most recent commit
 * so the operator opening the page next morning sees that the nightly
 * cron did (or didn't) commit anything overnight. Pre-fix, cron auto-
 * commits only left a pino log line; the UI gave zero signal that
 * something had run.
 */
function LastCycleFooter({ entry }: { entry: OptimizerHistoryRow | undefined }) {
  if (!entry) return null;
  return (
    <div className="optimizer-last-cycle" data-testid="optimizer-last-cycle">
      Último ciclo: {formatWhen(entry.committedAt)} · {actorLabel(entry.actor)} · {entry.dimCount}{" "}
      rubrica{entry.dimCount === 1 ? "" : "s"}, {entry.correctionCount} correç
      {entry.correctionCount === 1 ? "ão" : "ões"} fechada
      {entry.correctionCount === 1 ? "" : "s"}
    </div>
  );
}

function previewAffectedDims(parked: ParkedCorrectionRow[]): { dim: DimSlug; count: number }[] {
  const counts = new Map<DimSlug, number>();
  const evToDim: Record<string, DimSlug> = {
    academic: "academic",
    trajectory: "trajectory",
    tenure: "tenure",
    founder: "founder",
    project_management: "pm",
    stakeholder_management: "sm",
    business_acumen: "ba",
    hr_fluency: "hf",
    product_sense: "ps",
    pm: "pm",
    sm: "sm",
    ba: "ba",
    hf: "hf",
    ps: "ps",
  };
  let roleLevel = 0;
  for (const c of parked) {
    const dim = evToDim[c.evidenceId];
    if (!dim) continue;
    counts.set(dim, (counts.get(dim) ?? 0) + 1);
    roleLevel++;
  }
  if (roleLevel > 0) counts.set("role-level", roleLevel);
  return Array.from(counts.entries())
    .map(([dim, count]) => ({ dim, count }))
    .sort((a, b) => b.count - a.count);
}

function RunningPanel({
  state,
  cancel,
}: {
  state: OptimizerJobState;
  cancel: { mutate: () => void; isPending: boolean };
}) {
  const total = state.proposals.length;
  const done = state.proposals.filter(
    (p) =>
      p.status === "ready" ||
      p.status === "accepted" ||
      p.status === "rejected" ||
      p.status === "error",
  ).length;
  return (
    <div className="optimizer-running">
      <div className="optimizer-running-header">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        <strong>
          Ajustando {total} rubrica{total === 1 ? "" : "s"} · {done}/{total} prontas
        </strong>
        <Button
          size="sm"
          variant="outline"
          onClick={() => cancel.mutate()}
          disabled={cancel.isPending}
        >
          Cancelar
        </Button>
      </div>
      <ul className="optimizer-running-list">
        {state.proposals.map((p) => (
          <li key={p.dim} className={`optimizer-running-row is-${p.status}`}>
            <span className="optimizer-running-dim">{DIM_LABEL[p.dim]}</span>
            <span className="optimizer-running-status">
              {p.status === "pending" && "na fila"}
              {p.status === "fitting" && (
                <>
                  <Loader2 className="h-3 w-3 inline animate-spin mr-1" aria-hidden />
                  ajustando
                </>
              )}
              {p.status === "ready" && "pronto"}
              {p.status === "error" && "erro"}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function DimAccordion({
  state,
  expandedDim,
  onExpand,
}: {
  state: OptimizerJobState;
  expandedDim: DimSlug | null;
  onExpand: (dim: DimSlug | null) => void;
}) {
  return (
    <ul className="optimizer-accordion">
      {state.proposals.map((p) => (
        <li key={p.dim} className={`optimizer-accordion-row is-${p.status}`}>
          <button
            type="button"
            className="optimizer-accordion-head"
            onClick={() => onExpand(expandedDim === p.dim ? null : p.dim)}
            data-testid={`optimizer-row-${p.dim}`}
          >
            <span className="optimizer-accordion-dim">{DIM_LABEL[p.dim]}</span>
            <span className="optimizer-accordion-summary">
              {p.proposed ? (
                <span title="Acerto fora da amostra (current → proposed)">
                  Acerto {(p.current.metrics.looHitRate * 100).toFixed(0)}% →{" "}
                  <strong>{(p.proposed.metrics.looHitRate * 100).toFixed(0)}%</strong>
                </span>
              ) : p.status === "error" ? (
                <span className="text-rose-700">erro</span>
              ) : (
                "calculando…"
              )}
            </span>
            <span className={`optimizer-accordion-badge tone-${p.status}`}>
              {p.status === "accepted" && "aceito"}
              {p.status === "rejected" && "recusado"}
              {p.status === "ready" && "revisar"}
              {p.status === "error" && "erro"}
              {(p.status === "pending" || p.status === "fitting") && "calculando"}
            </span>
            {expandedDim === p.dim ? (
              <ChevronUp className="h-4 w-4" aria-hidden />
            ) : (
              <ChevronDown className="h-4 w-4" aria-hidden />
            )}
          </button>
          {expandedDim === p.dim && <DimProposalPanel proposal={p} />}
        </li>
      ))}
    </ul>
  );
}
